// Voice-activity classification for the own user's mic. This decides *what
// counts as speech* per audio frame; the timing of start/stop edges lives in the
// pure speaking-detector state machine. A plain energy gate fires on anything
// loud (typing, fans, music), so this combines:
//   Tier 1 — band-limited energy vs an adaptive noise floor (SNR), which rejects
//            steady background noise and rides out auto-gain drift; and
//   Tier 2 — a harmonicity (voiced-pitch) check, gated behind Tier 1 so the
//            pricier autocorrelation only runs on candidate-speech frames, to
//            reject sustained broadband non-speech (typing, hiss).

// ~43 ms time window at 48 kHz — long enough to autocorrelate down to an 85 Hz
// pitch (≈2 periods). Also sets the frequency resolution (~23 Hz/bin).
export const SPEAKING_FFT_SIZE = 2048;
export const SPEAKING_SMOOTHING = 0.85;

// Telephony-ish speech band used for the energy gate (Hz).
const SPEECH_BAND_LOW_HZ = 300;
const SPEECH_BAND_HIGH_HZ = 3400;
// Human voice fundamental range used for the harmonicity check (Hz).
const PITCH_MIN_HZ = 85;
const PITCH_MAX_HZ = 255;
// The harmonicity NSDF is the expensive part. Pitch (<=255 Hz) and its low
// harmonics sit far below ~4 kHz, so the time-domain frame is anti-aliased and
// downsampled by this integer factor before the autocorrelation — ~factor^2
// fewer multiply-adds (48 kHz -> ~8 kHz at factor 6) with the periodicity we
// measure left intact. Set to 1 to disable (full-rate), e.g. for A/B comparison.
const SPEAKING_DECIMATION_FACTOR = 6;

// Speech must exceed the adaptive noise floor by this factor (~+3.5 dB) — kept
// modest so quiet/onset speech registers quickly instead of waiting to get loud.
const SNR_MARGIN = 1.5;
// ...and clear this absolute normalized level, so near-silence never trips.
const ABSOLUTE_FLOOR = 0.02;
// NSDF peak above which a frame is considered voiced. Set so sustained vowels
// clear it comfortably while taps/clicks/typing and broadband noise — which only
// ever produce a brief, low-clarity periodicity — stay below it.
const VOICED_CLARITY_THRESHOLD = 0.6;
// Noise-floor adaptation: fall fast when the room quietens, rise slowly.
const FLOOR_FALL = 0.2;
const FLOOR_RISE = 0.02;

/**
 * Mean normalized magnitude (0–1) across the speech-band frequency bins. Pure;
 * exposed for testing.
 */
export const computeBandEnergy = (
    freqData: Uint8Array,
    sampleRate: number,
    fftSize: number,
): number => {
    const hzPerBin = sampleRate / fftSize;
    const lowBin = Math.max(1, Math.floor(SPEECH_BAND_LOW_HZ / hzPerBin));
    const highBin = Math.min(
        freqData.length - 1,
        Math.ceil(SPEECH_BAND_HIGH_HZ / hzPerBin),
    );
    if (highBin < lowBin) {
        return 0;
    }
    let sum = 0;
    for (let i = lowBin; i <= highBin; i++) {
        sum += freqData[i];
    }
    return sum / (highBin - lowBin + 1) / 255;
};

/**
 * Normalized square-difference (MPM/NSDF-style) peak over the human pitch lag
 * range — a [0,1] "is this voiced/periodic?" score. ~1 for a clean voiced vowel
 * or tone, near 0 for clicks, hiss, and broadband noise. Pure; exposed for
 * testing.
 */
export const computeHarmonicity = (
    timeData: Float32Array,
    sampleRate: number,
): number => {
    const minLag = Math.floor(sampleRate / PITCH_MAX_HZ);
    const maxLag = Math.min(
        Math.ceil(sampleRate / PITCH_MIN_HZ),
        timeData.length - 1,
    );
    let best = 0;
    for (let lag = minLag; lag <= maxLag; lag++) {
        const n = timeData.length - lag;
        let num = 0;
        let denom = 0;
        for (let i = 0; i < n; i++) {
            const a = timeData[i];
            const b = timeData[i + lag];
            num += a * b;
            denom += a * a + b * b;
        }
        if (denom > 0) {
            const clarity = (2 * num) / denom;
            if (clarity > best) {
                best = clarity;
            }
        }
    }
    return best;
};

export interface MicSpeechDetector {
    /**
     * Classify the current audio frame: true if it looks like voiced speech.
     * Pass the speaking state machine's current verdict (`utteranceSpeaking`) so
     * the noise floor stays frozen for the whole turn — see shouldFreezeFloor.
     */
    sample(utteranceSpeaking: boolean): boolean;
    /** Detach from the audio graph (caller closes the AudioContext). */
    disconnect(): void;
}

/**
 * Anti-aliased downsample of a time-domain frame by an integer `factor` via
 * boxcar averaging (a cheap low-pass; its first null is at sourceRate / factor).
 * Returns the input unchanged when `factor <= 1`. Writes into `out` when given a
 * correctly-sized buffer (reused across frames on the per-frame audio path so it
 * never allocates), otherwise allocates a fresh array. Exposed for testing.
 * Lets the harmonicity NSDF run on a shorter window over fewer lags without
 * disturbing the sub-4 kHz periodicity it scores.
 */
export const decimate = (
    data: Float32Array,
    factor: number,
    out?: Float32Array,
): Float32Array => {
    if (factor <= 1) {
        return data;
    }
    const outLength = Math.floor(data.length / factor);
    const target = out ?? new Float32Array(outLength);
    for (let j = 0; j < outLength; j++) {
        const base = j * factor;
        let sum = 0;
        for (let k = 0; k < factor; k++) {
            sum += data[base + k];
        }
        target[j] = sum / factor;
    }
    return target;
};

export interface EnergyFloorGate {
    /** True if `energy` clears the SNR threshold (a candidate for the voiced check). */
    isAbove(energy: number): boolean;
    /**
     * Fold this frame's energy into the adaptive noise floor — but only when not
     * `freeze`d, so the floor can't creep up to the speaker's own energy and
     * silence the detector. Call once per frame, after the speech decision; pass
     * `shouldFreezeFloor(...)` for `freeze`.
     */
    commit(energy: number, freeze: boolean): void;
    /** Current SNR threshold: floor * margin, clamped to the absolute floor. */
    threshold(): number;
    /** Current adaptive noise floor. Exposed for testing. */
    floor(): number;
}

/**
 * Whether floor adaptation should pause for this frame. Freeze on any
 * individually voiced frame, AND for the whole time the speaking state machine
 * considers the user to be talking — not just the voiced frames within a turn.
 * Continuous speech is peppered with unvoiced frames (consonants, transitions,
 * the sub-second gaps between words); folding those into the floor ratchets it
 * up toward the talker's own level until the SNR gate stops clearing and the
 * highlight drops mid-sentence. The state machine's stop gate keeps
 * `utteranceSpeaking` true across those gaps, so freezing on it holds the floor
 * at the genuine background for the whole turn. Pure; exposed for testing.
 */
export const shouldFreezeFloor = (
    frameIsVoiced: boolean,
    utteranceSpeaking: boolean,
): boolean => frameIsVoiced || utteranceSpeaking;

/**
 * Adaptive-noise-floor energy gate, factored out of the analyser closure so the
 * SNR threshold and the floor adaptation are testable in isolation (pure; no
 * audio deps). The floor rides toward steady background — falling fast when the
 * room quietens, rising slowly to absorb persistent noise — and is frozen while
 * speaking so it never climbs to the talker's own level. Defaults are the module
 * tuning constants; overridable for deterministic tests.
 */
export const createEnergyFloorGate = ({
    snrMargin = SNR_MARGIN,
    absoluteFloor = ABSOLUTE_FLOOR,
    floorFall = FLOOR_FALL,
    floorRise = FLOOR_RISE,
}: {
    snrMargin?: number;
    absoluteFloor?: number;
    floorFall?: number;
    floorRise?: number;
} = {}): EnergyFloorGate => {
    let noiseFloor = absoluteFloor;
    const threshold = (): number =>
        Math.max(absoluteFloor, noiseFloor * snrMargin);
    return {
        threshold,
        isAbove: (energy) => energy > threshold(),
        commit: (energy, freeze) => {
            if (freeze) {
                return;
            }
            const rate = energy < noiseFloor ? floorFall : floorRise;
            noiseFloor += (energy - noiseFloor) * rate;
        },
        floor: () => noiseFloor,
    };
};

/**
 * Wire `stream` into an analyser and classify each frame via the Tier 1 + Tier 2
 * pipeline above. The adaptive noise floor lives in an EnergyFloorGate carried
 * across calls; the per-frame voiced decision is instantaneous.
 */
export const createMicSpeechDetector = (
    audioContext: AudioContext,
    stream: MediaStream,
): MicSpeechDetector => {
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = SPEAKING_FFT_SIZE;
    analyser.smoothingTimeConstant = SPEAKING_SMOOTHING;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const freqData = new Uint8Array(analyser.frequencyBinCount);
    const timeData = new Float32Array(analyser.fftSize);
    // Reused scratch for the decimated frame (sized for the fixed factor), so the
    // per-frame Tier-2 path writes in place instead of allocating each call — same
    // convention as freqData/timeData above. See decimate.
    const voicedData = new Float32Array(
        Math.floor(SPEAKING_FFT_SIZE / SPEAKING_DECIMATION_FACTOR),
    );
    const { sampleRate } = audioContext;

    const energyGate = createEnergyFloorGate();

    return {
        sample(utteranceSpeaking) {
            analyser.getByteFrequencyData(freqData);
            const energy = computeBandEnergy(
                freqData,
                sampleRate,
                SPEAKING_FFT_SIZE,
            );

            let isSpeech = false;
            if (energyGate.isAbove(energy)) {
                // Gated Tier 2: confirm the loud frame is genuinely voiced. The
                // result is instantaneous (no smoothing/hold) — the speaking
                // detector's 1s stop gate bridges brief unvoiced consonants (s,
                // f) within an utterance, so a hold here would only serve to turn
                // an impulsive tap into a multi-frame run at onset.
                analyser.getFloatTimeDomainData(timeData);
                // Decimate before the NSDF: ~factor^2 fewer multiply-adds
                // (~0.68ms -> ~21us at factor 6), with the pitch-band periodicity
                // preserved (see SPEAKING_DECIMATION_FACTOR; set it to 1 for
                // full-rate). Writes into the reused `voicedData` scratch so the
                // per-frame path doesn't allocate. The original full-rate check is
                // kept commented out below for reference / quick A/B.
                const voicedRate = sampleRate / SPEAKING_DECIMATION_FACTOR;
                isSpeech =
                    computeHarmonicity(
                        decimate(
                            timeData,
                            SPEAKING_DECIMATION_FACTOR,
                            voicedData,
                        ),
                        voicedRate,
                    ) >= VOICED_CLARITY_THRESHOLD;
                // Full-rate alternative (no decimation):
                // isSpeech =
                //     computeHarmonicity(timeData, sampleRate) >=
                //     VOICED_CLARITY_THRESHOLD;
            }

            // Fold this frame into the adaptive noise floor (quiet, or
            // loud-but-not-voiced like typing). Frozen for the whole turn while
            // the user is speaking — not just on voiced frames — so the unvoiced
            // gaps of continuous speech can't ratchet the floor up to the
            // talker's level and silence the detector (see shouldFreezeFloor).
            energyGate.commit(
                energy,
                shouldFreezeFloor(isSpeech, utteranceSpeaking),
            );
            return isSpeech;
        },
        disconnect() {
            source.disconnect();
        },
    };
};
